"use client";

import React from "react";
import Autocomplete from "@/components/ui/autocomplete";
import { api } from "@/lib/trpc/client";
import { Card } from "@/components/ui/card";
import { BudgetEvolution } from "./BudgetEvolution";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { XCircle } from "lucide-react";
import { useTranslations } from "use-intl";
import { Icon } from "@iconify/react";
const maxYear = (() => {
  const currentMonth = new Date().getMonth();
  return currentMonth < 2 ? new Date().getFullYear() - 1 : new Date().getFullYear();
})();
const startingYear = 2016;
const availableYears = Array.from({
  length: maxYear - startingYear + 1
}, (_, i) => (maxYear - i).toString());
export function BudgetEvolutionCard({
  institutionId
}: {
  institutionId: string;
}) {
  const t = useTranslations();
  const [startYear, setStartYear] = React.useState<string | null>(null);
  const [endYear, setEndYear] = React.useState<string | null>(null);
  const budgetData = api.budgetPublicRouter.getAllTempBudgetData.useQuery({
    institutionId
  });
  const dataXY = (budgetData.data?.map(d => ({
    year: d.year ?? 0,
    revenue: d.data.venituri.total,
    expenses: d.data.cheltuieli.total
  }))?.filter(startYear ? d => d.year >= parseInt(startYear) : () => true)?.filter(endYear ? d => d.year <= parseInt(endYear) : () => true) || []) as {
    year: number;
    revenue: number;
    expenses: number;
  }[];
  const latestEntry = budgetData.data?.reduce((prev, curr) => (curr.year ?? 0) > (prev.year ?? 0) ? curr : prev, budgetData.data?.[0] ?? {
    year: 0,
    data: {
      venituri: {
        total: 0
      },
      cheltuieli: {
        total: 0
      }
    }
  });
  const isRevenueHigher = latestEntry ? latestEntry.data.venituri.total > latestEntry.data.cheltuieli.total : false;
  if (budgetData.isLoading) {
    return <Card className="flex h-full items-center justify-center p-8">
        <Icon icon="svg-spinners:pulse-rings-multiple" width="48" height="48" />
      </Card>;
  }
  return <Card className={`h-full w-full rounded-3xl ${isRevenueHigher ? "bg-green-500/10" : "bg-amber-50/70"}`} data-sentry-element="Card" data-sentry-component="BudgetEvolutionCard" data-sentry-source-file="BudgetEvolutionCard.tsx">
      <div className="flex flex-col items-center justify-between gap-2 space-y-1.5 rounded-t-lg p-6 desktop:flex-row">
        <div>
          <h3 className="text-2xl font-semibold leading-none tracking-tight text-glow">
            {t("Your Budget Evolution Title")}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("Your Budget Evolution subtitle")}
          </p>
        </div>
        <div className="flex w-full max-w-[260px] justify-center gap-3">
          <Popover data-sentry-element="Popover" data-sentry-source-file="BudgetEvolutionCard.tsx">
            <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="BudgetEvolutionCard.tsx">
              <Button variant={"outline"} className={cn("w-full bg-white pl-3 text-left font-normal")} data-sentry-element="Button" data-sentry-source-file="BudgetEvolutionCard.tsx">
                {!startYear && !endYear ? "Toți anii" : startYear && !endYear ? `Din ${startYear}` : !startYear && endYear ? `Până în ${endYear}` : `Din ${startYear} până în ${endYear}`}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="flex w-auto min-w-[400px] items-center p-0" align="center" data-sentry-element="PopoverContent" data-sentry-source-file="BudgetEvolutionCard.tsx">
              <div className="flex w-full items-center justify-center gap-4 p-4">
                <Autocomplete placeholder={startYear || "An început"} options={availableYears.map(year => ({
                label: year,
                value: year
              })).filter(year => !endYear || parseInt(year.value) <= parseInt(endYear))} initialValue={startYear} onChangeAction={value => setStartYear(String(value.value))} data-sentry-element="Autocomplete" data-sentry-source-file="BudgetEvolutionCard.tsx" />
                -
                <Autocomplete placeholder={endYear || "An sfârșit"} options={availableYears.map(year => ({
                label: year,
                value: year
              })).filter(year => !startYear || parseInt(year.value) >= parseInt(startYear))} initialValue={endYear} onChangeAction={value => setEndYear(String(value.value))} data-sentry-element="Autocomplete" data-sentry-source-file="BudgetEvolutionCard.tsx" />
                <Button variant={"link"} onClick={() => {
                setStartYear(null);
                setEndYear(null);
              }} className="p-0 px-1" data-sentry-element="Button" data-sentry-source-file="BudgetEvolutionCard.tsx">
                  <XCircle size={16} data-sentry-element="XCircle" data-sentry-source-file="BudgetEvolutionCard.tsx" />
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      <div className="px-4">
        <BudgetEvolution data={dataXY} data-sentry-element="BudgetEvolution" data-sentry-source-file="BudgetEvolutionCard.tsx" />
      </div>
    </Card>;
}